import { useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';

import type { CompPoints, UserStatus } from '@/api/auth';
import type {
  Bonus,
  Freespin,
  Jackpot,
  Lootbox,
  PlayerJackpotWin,
  PlayerTransaction,
} from '@/api/common';
import type { IPlayerBalance } from '@/modules/common/hooks/usePlayerAccounts';
import type { TopPlayer } from '@/modules/common/hooks/useTournaments';
import { useNotifyBonusReceived } from '@/modules/promotions/bonusNotification';
import { useGetCommonStore } from '@/store';
import { ChannelType, useSubscription } from './useSubscription';

export const useGlobalRealTimeSubscriptions = () => {
  const useCommonStore = useGetCommonStore();

  const {
    addPlayerJackpotWin,
    updateLootboxes,
    updateBonuses,
    updateFreespins,
    updatePlayerPayments,
    updateCompPoints,
    updateUserTournamentStatus,
    updateUserStatuses,
    updatePlayerBalance,
  } = useCommonStore(
    useShallow((state) => ({
      addPlayerJackpotWin: state.addPlayerJackpotWin,
      updateLootboxes: state.updateLootboxes,
      updateBonuses: state.updateBonuses,
      updateCompPoints: state.updateCompPoints,
      updateFreespins: state.updateFreespins,
      updatePlayerPayments: state.updatePlayerPayments,
      updateUserTournamentStatus: state.updateUserTournamentStatus,
      updateUserStatuses: state.updateUserStatuses,
      updatePlayerBalance: state.updatePlayerBalance,
    }))
  );

  const handleJackpotChange = useCallback((data: Jackpot) => {
    useCommonStore.setState({ activeJackpot: data });
  }, []);

  const handleJackpotWin = useCallback(
    (data: PlayerJackpotWin) => addPlayerJackpotWin(data),
    [addPlayerJackpotWin]
  );

  const notifyBonusReceived = useNotifyBonusReceived();

  const handleUpdateBonuses = useCallback(
    (data: Bonus) => {
      notifyBonusReceived(data, 'bonuses');
      updateBonuses(data);
    },
    [notifyBonusReceived, updateBonuses]
  );
  const handleUpdateFreespins = useCallback(
    (data: Freespin) => {
      notifyBonusReceived(data, 'freespins');
      updateFreespins(data);
    },
    [notifyBonusReceived, updateFreespins]
  );
  const handleUpdateLootboxes = useCallback(
    (data: Lootbox) => updateLootboxes(data),
    [updateLootboxes]
  );
  const handleUpdateCompPoints = useCallback(
    (data: CompPoints) => updateCompPoints(data),
    [updateCompPoints]
  );
  const handleUpdatePayments = useCallback(
    (data: PlayerTransaction) => updatePlayerPayments(data),
    [updatePlayerPayments]
  );
  const handleUpdateTournamentStatus = useCallback(
    (data: TopPlayer) => updateUserTournamentStatus(data),
    [updateUserTournamentStatus]
  );

  const handleUpdateUserStatuses = useCallback(
    (data: UserStatus[]) => updateUserStatuses(data),
    [updateUserStatuses]
  );

  const handleAnalytics = useCallback((context: any) => {
    console.log('######## $analytics#', context);
  }, []);

  const handleBalance = useCallback(
    (data: IPlayerBalance) => updatePlayerBalance(data),
    [updatePlayerBalance]
  );

  // --- Public Subscriptions ---
  useSubscription<Jackpot>(
    'public:jackpots_changes',
    handleJackpotChange,
    ChannelType.Public
  );
  // --- Private Subscriptions ---
  useSubscription<IPlayerBalance>('balance', handleBalance);
  useSubscription<Bonus>('bonuses_changes', handleUpdateBonuses);
  useSubscription<CompPoints>('comps_award', handleUpdateCompPoints);
  useSubscription<Freespin>('freespins_changes', handleUpdateFreespins);
  useSubscription<Lootbox>('lootboxes_changes', handleUpdateLootboxes);
  useSubscription<PlayerTransaction>('payments_changes', handleUpdatePayments);
  useSubscription<TopPlayer>(
    'tournaments_statuses',
    handleUpdateTournamentStatus
  );
  useSubscription<PlayerJackpotWin>('jackpot_win_award', handleJackpotWin);
  useSubscription<UserStatus[]>('groups_updates', handleUpdateUserStatuses);
  useSubscription('$analytics', handleAnalytics, ChannelType.Platform);
};
